import { IWithPostViewAnalyticsPayload } from '@cian/postview-analytics-utils';
import { useDeviceType } from '@cian/ui-kit';
import { TDevice } from '@cian/utils';
import { markAsClosed } from 'browser/map-search/utils/banner';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { AdfoxBannerWrapper } from '../../components/AdfoxBannerWrapper';
import { EBanners, EXPIRES_TIME_FOR_FSK, FSK_CLOSED_COOKIE_KEY } from '../../constants/banners';
import { FSK_BANNER, FSK_BANNER_PLACE } from '../../constants/fsk';
import { STONE_BANNER } from '../../constants/stone';
import { selectQueryString } from '../../selectors/queryString';
import { selectIsOpen } from '../../selectors/searchResults';
import { AdfoxBanner, PParams } from '../Adfox/components/AdfoxBanner';
import { AdfoxContextData, AdfoxContextProvider } from '../Adfox/context/AdfoxContext';
import { useSelectedAdfoxBannerConfig } from '../BannersContainer/utils/useSelectedAdfoxBannerConfig';
import { trackClickBanner, trackCloseBanner, trackShowBanner } from '../FskBannerContainer/tracking';
import { ProPlusBannerContainer } from '../ProPlusBannerContainer';

const P_PARAMS: Record<TDevice, PParams> = {
  desktop: { p1: 'dnoim', p2: 'jrxq' },
  phone: { p1: 'dnoin', p2: 'jrxq' },
  tablet: { p1: 'dnoin', p2: 'jrxq' },
};

const SIZE: Record<TDevice, AdfoxContextData['size']> = {
  desktop: { width: '240px', height: 400 },
  phone: { width: '100%', height: 64 },
  tablet: { width: '100%', height: 64 },
};

const ADFOX_BANNER_PLACE: Record<TDevice, string> = {
  desktop: 'bottom_right_map_corner',
  phone: 'above_header',
  tablet: 'above_header',
};

const BANNER_PLACE_BY_DEVELOPER: Record<number, Record<TDevice, string>> = {
  [FSK_BANNER.developerId]: FSK_BANNER_PLACE,
  [STONE_BANNER.developerId]: ADFOX_BANNER_PLACE,
};

const DEVELOPER_LABEL_MAP: Record<number, string> = {
  [FSK_BANNER.developerId]: 'newbuilding_banner_fsk',
  [STONE_BANNER.developerId]: 'newbuilding_banner_stone',
};

const getPostView = (deviceType: TDevice, developerId: number): IWithPostViewAnalyticsPayload => ({
  trackShowOncePerPage: true,
  banner: {
    id: developerId,
    name: 'Special_project',
    place: (BANNER_PLACE_BY_DEVELOPER[developerId] ?? ADFOX_BANNER_PLACE)[deviceType],
  },
});

interface IAdfoxSpecialProjectContainerProps {
  open: boolean;
  onClose(): void;
}

export function AdfoxSpecialProjectContainer({ open, onClose }: IAdfoxSpecialProjectContainerProps) {
  const deviceType = useDeviceType();
  const queryString = useSelector(selectQueryString);
  const isSearchResultsOpen = useSelector(selectIsOpen);
  const selectedConfig = useSelectedAdfoxBannerConfig();
  const isDesktop = deviceType === 'desktop';

  const [adfoxDegraded, setAdfoxDegraded] = React.useState(false);

  const developerId = selectedConfig?.developerId ?? FSK_BANNER.developerId;
  const trackingLabel = DEVELOPER_LABEL_MAP[developerId];

  const handleClose = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      markAsClosed(FSK_CLOSED_COOKIE_KEY, EBanners.Fsk, EXPIRES_TIME_FOR_FSK);
      onClose();
      if (trackingLabel) {
        trackCloseBanner(queryString, deviceType, trackingLabel);
      }
    },
    [deviceType, queryString, onClose, trackingLabel],
  );
  const handleClick = React.useCallback(() => {
    if (trackingLabel) {
      trackClickBanner(queryString, deviceType, trackingLabel);
    }
  }, [deviceType, queryString, trackingLabel]);
  const handleDegrade = React.useCallback(() => setAdfoxDegraded(true), []);

  const postViewAnalyticsData = React.useMemo(() => getPostView(deviceType, developerId), [deviceType, developerId]);

  React.useEffect(() => {
    if (trackingLabel) {
      trackShowBanner(queryString, deviceType, trackingLabel);
    }

    // Не добавляю зависимости, чтобы аналитика отправилась только один раз при показе
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!open) {
    return null;
  }

  if (adfoxDegraded) {
    return isDesktop ? null : <ProPlusBannerContainer open={open} onClose={onClose} />;
  }

  return (
    <AdfoxContextProvider
      pParams={P_PARAMS[deviceType]}
      size={SIZE[deviceType]}
      postViewAnalyticsData={postViewAnalyticsData}
      isSearchResultsOpen={isSearchResultsOpen}
      onTemplateClick={handleClick}
      onTemplateClose={handleClose}
    >
      <AdfoxBannerWrapper>
        <AdfoxBanner onStub={handleDegrade} onError={handleDegrade} />
      </AdfoxBannerWrapper>
    </AdfoxContextProvider>
  );
}
